// Sharing a match as a code, so a duel can be played by correspondence.
//
// The code carries the match STATE, not a recording of it. The seed and the round
// number rebuild the city and the wind; the craters travel as a list, because
// they are holes punched into the buildings and nothing can derive them.
//
// Nothing is re-simulated on purpose. The two players' gorillas are doing their
// own antics at their own pace, and an antic moves the hitbox — so replaying the
// shots on the other machine would NOT reproduce this duel. Handing over the
// result does. That is also why this works with no server: the code is not a
// reference to the match, it is the match.

import { state } from './state.js';
import { t } from './i18n.js';
import { applyMode, MODES } from './modes.js';
import { makeCity } from './city.js';
import { crater } from './effects.js';
import { adoptRivalLook, dealCpuKit, myLook, resetSpecialUses, savedTargetWins, savedTurnSeconds } from './progress.js';
import { ACCESSORIES, SPECIES, TARGET_WINS_CHOICES, TARGET_WINS_DEFAULT, TURN_SECONDS_CHOICES } from './config.js';
import { ALPHABET, randomSeed } from './rng.js';

const PREFIX = 'QBG1-';
const VERSION = 1;
/** A code is pasted into a chat window; anything longer than this is a typo. */
const MAX_CODE = 4000;
/** Sanity bound on the damage list, so a doctored code cannot stall the tab. */
const MAX_CRATERS = 400;

const isInt = (v, lo, hi) => Number.isInteger(v) && v >= lo && v <= hi;

/** Base64, in the URL-safe flavour that survives being pasted into chat. */
function toBase64Url(text) {
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text) {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  return atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
}

/** The code for the match exactly as it stands. */
export function encodeMatch() {
  const payload = {
    v: VERSION,
    /** seed */ s: state.matchSeed,
    /** mode */ m: state.mode,
    /** round */ r: state.round,
    /** turn */ t: state.turn,
    /** rounds won, per side */ w: [...state.matchWins],
    /** whether the two of them have traded places */ p: state.swapped ? 1 : 0,
    /** hits this round, per side */ h: [...state.score],
    /** craters, as [x, y, r] */ c: state.craters.map(c => [Math.round(c.x), Math.round(c.y), Math.round(c.r * 10) / 10]),
    /**
     * How many rounds this match is won by, and 0 for one that does not end.
     *
     * It travels because it is a rule and not a preference: the two machines have to stop at the
     * same time, and one of them ending while the other plays on is the one disagreement a match
     * cannot absorb. The invitation does NOT carry it — the 4-4 code packs its mode and its clock
     * into a single letter, and a third choice does not fit in 26 letters without two of them
     * meaning the same thing — so the first code that crosses settles it, which is before anybody
     * has won anything.
     */
    /**
     * Si la caja de esta ronda ya la ha abierto alguien.
     *
     * Tiene que viajar porque la caja no viaja: la siembra la ronda entera y las dos máquinas la
     * ponen en el mismo sitio, así que un código que no dijera nada sobre ella la volvía a dejar
     * cayendo en la máquina del otro. Medido: el jugador abría el regalo, mandaba el código, y el
     * regalo volvía a caer al otro lado — una recompensa por ronda y por cabeza. Con el clic, la
     * primera forma de abrirla pasa a ser un toque, y un toque llega antes que un plátano.
     *
     * Un código viejo, sin `k`, significa que no se ha abierto, que es lo que significaba cuando
     * se escribió.
     */
    k: state.crateTaken ? 1 : 0,
    x: state.targetWins,
    /**
     * El reloj de la partida, en segundos, y 0 para la que no tiene.
     *
     * Viaja porque es una regla y no una preferencia, como las rondas: sin él cada máquina contaba
     * con su propio ajuste — un duelo por código podía tener 15 segundos de un lado y 45 del otro —
     * y, además, el ajuste es un valor vivo, así que tocarlo en AJUSTES movía el reloj de la
     * partida en curso. Un código viejo, sin `sec`, se queda con el ajuste local, que es
     * exactamente lo que hacía cuando el campo no existía.
     */
    sec: state.turnSeconds,
    /**
     * What the two sides have standing, as [idx, at] or 0: shield, spring, mushroom, force
     * field, in that order.
     *
     * They travel because the machine that resolves a shot is the one that threw it, and a
     * defence it has not been told about is a defence that does not exist — measured, a force
     * field raised on this side was nowhere on the other one, so the banana landed and the
     * round ended with the defence still in the hand of the player who had put it up. The two
     * are the same list the drawing and the hit test read (`state.shield` and the three beside
     * it), so there is nothing here to keep in step by hand.
     *
     * `at` is this machine's clock, which the other one does not share; it is only the phase
     * of the shield's shimmer, and a defence that protects is one whatever it looks like.
     */
    // El cuarto hueco era el superguerrero, y se queda vacío a propósito: el índice es lo que
  // hace que un código viejo se lea en el mismo sitio. Un código nuevo escribe null aquí y el
  // juego lee null, que es lo mismo que no llevar defensa.
  f: [state.shield, state.spring, state.mushroom, null]
      .map(p => (p ? [p.idx ? 1 : 0, Math.round(p.at)] : 0)),
    /**
     * Y el dron, si lo hay en el aire: un bit. La máquina del que tira resuelve el daño de su
     * propia bomba y lo manda en el marcador, como con cualquier otro tiro; lo que no viajaba
     * era la imagen, y el de enfrente se comía una ronda sin haber visto pasar nada.
     */
    dr: state.drone || state.bomb ? 1 : 0,
    /**
     * Y las vidas extra que se ha encontrado cada uno por el camino, que son estado de la
     * partida como las defensas: sin esto, el que las cogía las veía desaparecer en la otra
     * máquina y moría en la ronda siguiente con el colchón que había ganado.
     */
    lv: state.lives.map(n => Math.max(0, Math.min(9, Math.round(n)))),
  };

  // Whose code this is, in a duel by correspondence: the side of the one who wrote it, so that
  // whoever loads it knows which side is theirs — and the gorilla that side is in, because the
  // other machine draws it and the picture is the hitbox.
  if (state.byCode) {
    payload.d = state.seat ? 1 : 0;
    payload.lk = myLook();
  }

  return PREFIX + toBase64Url(JSON.stringify(payload));
}

/**
 * Parses a code, throwing something the player can act on rather than limping
 * along with half a match.
 */
export function decodeMatch(text) {
  const raw = String(text || '').trim();
  if (!raw) throw new Error(t('code.noCode'));
  if (raw.length > MAX_CODE) throw new Error(t('code.tooLong'));

  const body = raw.toUpperCase().startsWith(PREFIX) ? raw.slice(PREFIX.length) : raw;

  let data;
  try {
    data = JSON.parse(fromBase64Url(body));
  } catch {
    throw new Error(t('code.notACode'));
  }

  if (!data || data.v !== VERSION) throw new Error(t('code.version'));
  if (typeof data.s !== 'string' || !data.s.length || data.s.length > 24) throw new Error(t('code.corrupt'));
  if (!MODES.some(m => m.id === data.m)) throw new Error(t('code.mode'));
  if (!isInt(data.r, 1, 99) || !isInt(data.t, 0, 1)) throw new Error(t('code.corrupt'));
  if (!Array.isArray(data.w) || data.w.length !== 2 || !data.w.every(n => isInt(n, 0, 99))) throw new Error(t('code.corrupt'));
  // Optional on purpose: a code written before the swap existed has no `p` in it, and
  // it means exactly what it used to mean — the two of them on their own perches.
  if (data.p !== undefined && !isInt(data.p, 0, 1)) throw new Error(t('code.corrupt'));
  // And the same for the author of a duel: absent in a code from a single-machine match.
  if (data.d !== undefined && !isInt(data.d, 0, 1)) throw new Error(t('code.corrupt'));
  // And optional the same way: a code written before a match had a length to choose has no `x` in
  // it, and it means three rounds, which is what it meant when it was written.
  if (data.x !== undefined && !isInt(data.x, 0, 99)) throw new Error(t('code.corrupt'));
  if (data.sec !== undefined && !isInt(data.sec, 0, 600)) throw new Error(t('code.corrupt'));
  // Y el dron, opcional como todo lo que se añadió después: un código viejo no lo trae y
  // significa que no había ninguno en el aire.
  if (data.dr !== undefined && !isInt(data.dr, 0, 1)) throw new Error(t('code.corrupt'));
  // Y las vidas, igual de opcionales: un código escrito antes no las trae y quiere decir cero.
  if (data.lv !== undefined
      && (!Array.isArray(data.lv) || data.lv.length !== 2 || !data.lv.every(n => isInt(n, 0, 9)))) {
    throw new Error(t('code.corrupt'));
  }
  // Y la caja, opcional igual: un código escrito antes de que viajara no la trae, y no traerla
  // significa que nadie la ha abierto.
  if (data.k !== undefined && !isInt(data.k, 0, 1)) throw new Error(t('code.corrupt'));
  // The defences are optional the same way: a code written before they travelled has none of
  // them in it, and it means exactly what it used to mean.
  if (data.f !== undefined
      && (!Array.isArray(data.f) || data.f.length !== 4 || !data.f.every(p => p === 0
        || (Array.isArray(p) && p.length === 2 && isInt(p[0], 0, 1)
          && typeof p[1] === 'number' && Number.isFinite(p[1]))))) {
    throw new Error(t('code.corrupt'));
  }
  if (!Array.isArray(data.c) || data.c.length > MAX_CRATERS) throw new Error(t('code.corrupt'));

  data.c = data.c.filter(c => Array.isArray(c) && c.length === 3 && c.every(n => typeof n === 'number' && Number.isFinite(n)));

  return data;
}

/**
 * Rebuilds a decoded match in place: same mode, same city, same wind, same
 * damage, same score, and the turn handed to whoever the code says.
 *
 * Deliberately silent. A crater applied here punches the bitmap only — the bang
 * and the debris belonged to the shot that made it, on someone else's machine.
 */
export function applyMatch(data) {
  applyMode(data.m);

  state.matchWins = [data.w[0], data.w[1]];
  state.score = Array.isArray(data.h) && data.h.length === 2 ? [data.h[0], data.h[1]] : [0, 0];
  state.round = data.r;
  state.matchSeed = data.s;
  state.matchOver = false;
  // What this side has armed, and the charges it has left, are its own: neither travels in a
  // code, because the shot that arrives was aimed with the other side's bag. Rebuilding them on
  // every code did two things, and both were wrong — it disarmed the special the player was
  // holding (the button stopped saying what it would do) and it handed back every charge that
  // had been spent, once per turn, which is a system of charges that never charges. They are
  // rebuilt only when there was no match here to keep them for, which is the case the reset was
  // written for: a code that arrives with nothing behind it is a new match.
  if (!state.matchLive) {
    state.special = [null, null];
    state.guard = [null, null];
    state.specialUses = [resetSpecialUses(), state.byCode ? resetSpecialUses() : dealCpuKit()];
  }

  // Read before the city is built, because building it is what places the two of
  // them on their perches — and a swap is one bit rather than two stored places, so
  // the rooftops themselves are still derived from the skyline.
  state.swapped = data.p === 1;

  // The length of the match, from the code: a code written before the choice existed has none in it,
  // and it means exactly what it used to mean — three rounds.
  state.targetWins = isInt(data.x, 0, 99) ? data.x : TARGET_WINS_DEFAULT;
  // Y el reloj de la partida, que es lo que hace que las dos máquinas cuenten lo mismo.
  state.turnSeconds = isInt(data.sec, 0, 600) ? data.sec : savedTurnSeconds();

  // Rebuilds the city from the seed and clears the crater list along with it.
  makeCity();

  // Y la caja, que `makeCity()` acaba de volver a poner en el aire: si el código dice que esta
  // ronda ya la abrió alguien, aquí no hay nada que abrir. Va DESPUÉS de `makeCity()` porque es
  // `spawnCrate()` quien siembra la caja y quien borra la marca — al revés no quedaría nada que
  // borrar. Ver `k` en encodeMatch.
  if (data.k === 1) {
    state.crate = null;
    state.crateTaken = true;
  }

  // And whatever the two of them were standing behind, which `makeCity()` has just thrown
  // away with the city it belongs to. Read from the code rather than kept from before, so a
  // code that arrives with a new round in it arrives with no defences in it either.
  const standing = i => (Array.isArray(data.f) && Array.isArray(data.f[i])
    ? { idx: data.f[i][0], at: data.f[i][1] }
    : null);
  state.shield = standing(0);
  state.spring = standing(1);
  state.mushroom = standing(2);

  // Y las vidas extra, que sí se quedan de una ronda para la siguiente dentro de la partida.
  state.lives = Array.isArray(data.lv) ? [data.lv[0], data.lv[1]] : [0, 0];

  // Then put the damage back.
  data.c.forEach(([x, y, r]) => {
    const b = state.buildings.find(bb => x >= bb.x && x <= bb.x + bb.w && y >= bb.y && y <= bb.y + bb.h);
    if (b) crater(b, x, y, r);
  });

  state.turn = data.t;
  state.turnStartedAt = state.clock;
}

/** One-line summary of who is where, for the status bar after a load. */
export function describeMatch(data) {
  return `ROUND ${String(data.r).padStart(2, '0')} · WIN 1: ${data.w[0]} · WIN 2: ${data.w[1]}`;
}

// --- the invitation code ---------------------------------------------------
//
// Two people arranging a duel need something they can say out loud, and the code that carries a
// match is a hundred and fifty characters of base64 meant to be pasted. So a duel starts on a
// code of its own, 4-4, and hands the match over in the usual long ones from then on.
//
// It is not a smaller match, it is a FRESH one. The city, the moon, the wind and the crate all
// come out of the round's seed, so three readable characters of seed plus the character of the
// other player, the mode and the clock are the whole of what a duel nobody has played yet has to
// say: the damage, the score and the turn do not exist yet.
//
//    K7M2-QP4B
//    \___/ \__/
//      |    | \_ the mode and the clock, mixed into one character, then a check character
//      |    \___ the other player, as they are dressed: species and accessories in one number
//      \___ the seed of the round, and the whole of it
//
// The alphabet is the one randomSeed() uses, which exists to be read out loud: no I, no O, no 0
// and no 1, because those are the ones people hear wrong. A check character catches a single
// mistyped letter, so one wrong keystroke cannot start a different duel by accident.
//
// The packing is tight, which is why it is written down: thirteen species and eleven accessories
// come to 13 x 2048 = 26,624 and three characters hold 32,768. Two accessories too many and the
// character has to be spent on a fourth, which comes off the seed.
//
const MODE_IDS = ['modern', 'classic', 'demolition', 'lunar', 'weather', 'quake'];
const INVITE_LEN = 8;
const PACK_LEN = 3;

// Written out rather than read off the cards, because this is the one place a mode is a number and
// numbers have to keep meaning what they meant. Following the setup screen's order here would mean
// that moving a card rewrites every code already in someone's clipboard: the letter that used to
// say DEMOLITION would come out as QUAKE on the other phone. The two lists only have to agree on
// their members — six modes and four clocks, `mi + 6 * ti` is 23 at worst, so the pair still fits
// in one character (four clocks instead of twelve is what that budget is spent on).
if (MODES.some(m => !MODE_IDS.includes(m.id))) {
  // Loud but not fatal: a mode the packing has never heard of would have `indexOf` answer -1 and
  // the code would quietly name the first mode instead. The cards still work, the codes lose one.
  console.error('matchcode: mode missing from MODE_IDS:', MODES.map(m => m.id).filter(id => !MODE_IDS.includes(id)).join(', '));
}

function inviteLetters(text) {
  return String(text || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** One character per place: a number in base 32, most significant first. */
function packNumber(n, places) {
  const out = [];
  let rest = Math.max(0, Math.floor(n));

  for (let i = 0; i < places; i += 1) {
    out.unshift(ALPHABET[rest % ALPHABET.length]);
    rest = Math.floor(rest / ALPHABET.length);
  }

  return out;
}

function unpackNumber(letters) {
  return letters.reduce((n, ch) => n * ALPHABET.length + Math.max(0, ALPHABET.indexOf(ch)), 0);
}

/** A character and what it is wearing as one number: species plus a bit per accessory worn. */
function packLook(look) {
  const worn = (look && look.worn) || {};
  const ids = new Set(Object.values(worn));
  let mask = 0;

  ACCESSORIES.forEach((a, i) => { if (ids.has(a.id)) mask |= (1 << i); });
  return Math.max(0, (((look && look.species) || 0) + SPECIES.length * mask));
}

function unpackLook(n) {
  const species = n % SPECIES.length;
  const mask = Math.floor(n / SPECIES.length);
  const worn = {};

  // One per slot: a doctored code claiming two hats loses all but the last, which is the rule the
  // wardrobe keeps anyway.
  ACCESSORIES.forEach((a, i) => { if (mask & (1 << i)) worn[a.slot] = a.id; });
  return { species, worn };
}

/** A weighted sum of the rest, so a single mistyped letter cannot pass for a duel. */
function inviteCheck(letters) {
  let sum = 0;
  letters.forEach((ch, i) => { sum += (ALPHABET.indexOf(ch) + 1) * (i + 2); });
  return ALPHABET[sum % ALPHABET.length];
}

/** The 4-4 code for a duel nobody has played yet, with the match's length in it. */
export function encodeInvite({ seed, mode, seconds, look, targetWins }) {
  const mi = Math.max(0, MODE_IDS.indexOf(mode));
  const ti = Math.max(0, TURN_SECONDS_CHOICES.indexOf(seconds));
  const letters = [
    ...String(seed).toUpperCase().slice(0, 3).padEnd(3, ALPHABET[0]),
    ...packNumber(packLook(look), PACK_LEN),
    ALPHABET[(mi + MODE_IDS.length * ti) % ALPHABET.length],
  ];

  // La longitud de la partida, en una letra propia y antes del control. Los códigos que ya están
  // repartidos tienen ocho letras —siete de datos y el control— y se siguen leyendo igual: esta
  // letra solo se añade cuando hay rondas que contar, y entonces el control pasa a cubrir ocho
  // datos. Antes el invitado jugaba con su propio ajuste hasta el primer código largo, así que un
  // "primero en siete" del anfitrión duraba lo que el que aceptaba tuviera puesto.
  const wants = TARGET_WINS_CHOICES.indexOf(targetWins);
  if (wants >= 0) letters.push(ALPHABET[wants]);

  letters.push(inviteCheck(letters));

  const body = letters.join('');
  return body.slice(0, 4) + '-' + body.slice(4);
}

/**
 * The invitation and the match it describes, for the one who invites.
 *
 * The code comes first and the match is built from it: the invitation names the city, and two
 * machines that each built their own would be aiming at different skylines. The turn is left on
 * the side that is not here, which is what the code is sent for — and that side is dressed in
 * whatever this machine is wearing, because the invitation is the only chance to say it and the
 * one who answers is aiming at that gorilla from their first turn.
 */
export function newInvite() {
  const invite = {
    seed: randomSeed().slice(0, 3), mode: state.mode, seconds: savedTurnSeconds(), look: myLook(),
    // Y el largo de la partida, que viaja con la invitación como el reloj.
    targetWins: savedTargetWins(),
  };

  applyInvite(invite);
  return encodeInvite(invite);
}

/**
 * True for a code short enough to be an invitation rather than a match.
 *
 * Length is the whole of the test: a match code is over a hundred characters and this one is
 * nine at most, so nothing has to be guessed at and no prefix is needed.
 */
export function isInvite(text) {
  const letters = inviteLetters(text);
  return letters.length > 0 && letters.length <= 12;
}

/**
 * Una invitación escrita con la forma con la que salió del generador.
 *
 * El anfitrión abre la sala `K7M2-QP4B`; quien la recibe puede teclearla sin el guion, con un
 * espacio en medio o en minúsculas, y es la misma invitación — así que tiene que ser la misma
 * sala. El servidor normaliza por su cuenta, pero el cliente abría `K7M2QP4B` mientras el otro
 * esperaba en `K7M2-QP4B`: dos salas de un jugador cada una y ninguno de los dos veía al otro.
 */
export function canonicalInvite(text) {
  // Nueve letras como mucho, que es lo que lleva un código con rondas: cortarlo a ocho dejaría
  // fuera la partida y abriría una sala distinta de la del anfitrión.
  const letters = inviteLetters(text).slice(0, INVITE_LEN + 1);
  if (letters.length < INVITE_LEN) return String(text || '').trim();
  return letters.slice(0, 4) + '-' + letters.slice(4);
}

/** Reads one back, or throws something the player can act on. */
export function decodeInvite(text) {
  const letters = [...inviteLetters(text)];

  // Ocho letras o nueve: el largo de siempre y el que lleva las rondas.
  if (letters.length !== INVITE_LEN && letters.length !== INVITE_LEN + 1) {
    throw new Error(t('code.inviteLength'));
  }

  // El control es el último carácter y cubre todos los anteriores, sean siete u ocho.
  const data = letters.slice(0, -1);
  if (data.some(ch => !ALPHABET.includes(ch))) throw new Error(t('code.inviteLetters'));
  if (letters[data.length] !== inviteCheck(data)) throw new Error(t('code.inviteCheck'));

  const mixed = ALPHABET.indexOf(letters[6]);

  return {
    seed: letters.slice(0, 3).join(''),
    look: unpackLook(unpackNumber(letters.slice(3, 3 + PACK_LEN))),
    mode: MODE_IDS[mixed % MODE_IDS.length],
    seconds: TURN_SECONDS_CHOICES[Math.floor(mixed / MODE_IDS.length) % TURN_SECONDS_CHOICES.length],
    // Ausente en un código de ocho letras: el invitado juega con su propia longitud, que es lo
    // que hacía antes de que esto existiera.
    targetWins: data.length === INVITE_LEN
      ? TARGET_WINS_CHOICES[Math.max(0, ALPHABET.indexOf(letters[INVITE_LEN - 1]))]
      : undefined,
  };
}

/**
 * Builds the duel an invitation describes: a fresh round from the seed it carries.
 *
 * Called on a machine that is already in a match of its own — the one the invitation replaces —
 * for the same reason `applyMatch` is, and it leaves the turn with the side that just arrived,
 * which is what the inviter sent the code for.
 */
export function applyInvite(data) {
  applyMode(data.mode);

  // El reloj, que la invitación sí lleva: el modo y el reloj son lo que cabe en su letra combinada.
  // Sin esto el invitado empezaba con su propio reloj hasta el primer código largo, que era el
  // único que lo traía.
  if (isInt(data.seconds, 0, 600)) state.turnSeconds = data.seconds;

  // Y las rondas, si la invitación las trae. Un código de ocho letras no las trae y se queda con
  // el ajuste local, que es exactamente lo que hacía antes de que existiera este campo.
  if (isInt(data.targetWins, 0, 99)) state.targetWins = data.targetWins;

  state.matchWins = [0, 0];
  state.score = [0, 0];
  state.round = 1;
  state.matchSeed = data.seed;
  state.matchOver = false;
  state.matchLive = true;
  state.special = [null, null];
  state.guard = [null, null];
  state.specialUses = [resetSpecialUses(), resetSpecialUses()];
  state.swapped = false;

  // Who the other one is, which the invitation is the only chance to say: three characters of a
  // gorilla, and the side that answers is aiming at it from their first turn.
  adoptRivalLook(data.look);

  // Rebuilds the city from the seed and clears the crater list along with it.
  makeCity();

  state.turn = 1;
  state.turnStartedAt = state.clock;
}
